/**
 * BT001 #19758 — "this signed-in account has no entitlement to the client portal at all".
 *
 * Two routes lead here, and both must end on the Access denied screen, never on another sign-in:
 *
 *  1. The API rejects the shell probe — GET /api/v1/client-it/me answers 403/404
 *     (handled in ClientTiersProvider).
 *  2. Entra refuses the API TOKEN itself with AADSTS50105 ("the signed-in user is not assigned to a
 *     role for the application") — which is what happens once the API app carries
 *     "Assignment required" (#19759). The sign-in (openid profile) still succeeds, so the refusal
 *     only surfaces at token acquisition: from acquireTokenSilent, or as a handleRedirectPromise
 *     rejection after an acquireTokenRedirect.
 *
 * Route 2 used to loop forever: useAuth answered the interaction-required error with
 * acquireTokenRedirect, Entra refused, the user came back, the next API call failed, it redirected
 * again. A refused token is operator decision (b), "no entitlement at all" — so it is converted to
 * AccessDeniedError and NO further redirect is issued.
 *
 * Only AADSTS50105 is treated this way. Every other token failure keeps its existing handling —
 * a transient failure must never deny (decision (c)).
 */

/** Entra's "user is not assigned to a role for this application" code. */
export const ENTITLEMENT_REFUSED_CODE = 'AADSTS50105';

export class AccessDeniedError extends Error {
  constructor(message = 'This account is not entitled to the client portal') {
    super(message);
    this.name = 'AccessDeniedError';
  }
}

export function isAccessDenied(error: unknown): error is AccessDeniedError {
  return error instanceof AccessDeniedError;
}

/**
 * True when an MSAL error is Entra refusing the token for lack of an app-role assignment.
 * MSAL surfaces the AADSTS code in `errorMessage` / `message` (and occasionally `subError`), not in
 * `errorCode` (which carries the OAuth error, e.g. `invalid_grant`), so all of them are read.
 */
export function isEntitlementRefusal(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { errorCode?: unknown; errorMessage?: unknown; subError?: unknown; message?: unknown };
  return [e.errorCode, e.errorMessage, e.subError, e.message].some(
    (v) => typeof v === 'string' && v.includes(ENTITLEMENT_REFUSED_CODE),
  );
}

/*
 * Set when handleRedirectPromise rejects with AADSTS50105 at startup (msalInstance.ts). The account
 * is still signed in (the sign-in itself succeeded), so without this the very next API call would
 * acquireTokenSilent → interaction required → acquireTokenRedirect → refused again: the loop.
 * Module-scoped on purpose — it lives exactly as long as this page load, and logoutRedirect()
 * (the only way out of the Access denied screen) navigates away and resets it.
 */
let deniedAtRedirect = false;

export function markAccessDeniedAtRedirect(): void {
  deniedAtRedirect = true;
}

export function wasAccessDeniedAtRedirect(): boolean {
  return deniedAtRedirect;
}

/** Test seam only. */
export function resetAccessDeniedAtRedirect(): void {
  deniedAtRedirect = false;
}
